import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { createSelfAccount } from '../../services/mentorService';
import Button from '../../components/common/Button/Button';
import cosmicLogo from '../../assets/images/cosmic-logo.png';
import styles from './MentorOnboarding.module.css';

const MentorOnboarding = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: user?.email || '',
    phone: '',
    whatsapp_phone: '',
    linkedin_profile: '',
    description: '',
    timezone: '',
    max_students: 5,
    years_of_experience: 0,
    highest_qualification: '',
    institution_organization: '',
    skills: '', // Will split by comma
    track: [], // Will be an array with a single element
    domain: 'Software',
    preferred_project_types: '' // Will split by comma
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.details_added) {
      navigate('/mentor/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.email) {
      setFormData((prev) => ({
        ...prev,
        email: user.email
      }));
    }
  }, [user?.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        skills: formData.skills ? formData.skills.split(',').map((s) => s.trim()).filter(Boolean) : [],
        track: formData.track, // It's already an array from the select
        preferred_project_types: formData.preferred_project_types ? formData.preferred_project_types.split(',').map((s) => s.trim()).filter(Boolean) : [],
        max_students: parseInt(formData.max_students, 10),
        years_of_experience: parseInt(formData.years_of_experience, 10),
      };

      if (!payload.whatsapp_phone) {
        delete payload.whatsapp_phone;
      }
      delete payload.email;

      await createSelfAccount(payload);
      await refreshUser();
      navigate('/mentor/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete onboarding. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className={styles.onboardingContainer}>
      <div className={styles.onboardingCard}>
        <div className={styles.header}>
          <img src={cosmicLogo} alt="COSMIC Logo" className={styles.logoImage} />
          <h1 className={styles.logo}>COSMIC</h1>
          <p className={styles.subtitle}>Complete Your Mentor Profile</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="first_name" className={styles.label}>First Name *</label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                value={formData.first_name}
                onChange={handleChange}
                className={styles.input}
                placeholder="First Name"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="last_name" className={styles.label}>Last Name *</label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                value={formData.last_name}
                onChange={handleChange}
                className={styles.input}
                placeholder="Last Name"
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>Email *</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              className={styles.input}
              disabled
              required
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="phone" className={styles.label}>Phone Number *</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="+1234567890"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="whatsapp_phone" className={styles.label}>WhatsApp Number</label>
              <input
                id="whatsapp_phone"
                name="whatsapp_phone"
                type="tel"
                value={formData.whatsapp_phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="+1234567890"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="linkedin_profile" className={styles.label}>LinkedIn Profile URL</label>
            <input
              id="linkedin_profile"
              name="linkedin_profile"
              type="url"
              value={formData.linkedin_profile}
              onChange={handleChange}
              className={styles.input}
              placeholder="https://linkedin.com/in/username"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description" className={styles.label}>Professional Summary</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
              placeholder="Tell us about your professional background and why you want to mentor."
              rows="4"
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="years_of_experience" className={styles.label}>Years of Experience</label>
              <input
                id="years_of_experience"
                name="years_of_experience"
                type="number"
                min="0"
                value={formData.years_of_experience}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="max_students" className={styles.label}>Max Students</label>
              <input
                id="max_students"
                name="max_students"
                type="number"
                min="1"
                max="20"
                value={formData.max_students}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="timezone" className={styles.label}>Timezone</label>
            <input
              id="timezone"
              name="timezone"
              type="text"
              value={formData.timezone}
              onChange={handleChange}
              className={styles.input}
              placeholder="e.g., UTC-5, EST, etc."
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="skills" className={styles.label}>Skills (comma separated)</label>
            <input
              id="skills"
              name="skills"
              type="text"
              value={formData.skills}
              onChange={handleChange}
              className={styles.input}
              placeholder="e.g., React, Python, Data Science"
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="track" className={styles.label}>Track</label>
              <select
                id="track"
                name="track"
                value={formData.track[0] || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, track: [e.target.value] }))}
                className={styles.input}
                required
              >
                <option value="" disabled>Select a track</option>
                <option value="research">Research</option>
                <option value="product">Product</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="domain" className={styles.label}>Domain</label>
              <select
                id="domain"
                name="domain"
                value={formData.domain}
                onChange={handleChange}
                className={styles.input}
              >
                <option value="Software">Software</option>
                <option value="Hardware">Hardware</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="preferred_project_types" className={styles.label}>Preferred Project Types (comma separated)</label>
            <input
              id="preferred_project_types"
              name="preferred_project_types"
              type="text"
              value={formData.preferred_project_types}
              onChange={handleChange}
              className={styles.input}
              placeholder="e.g., Web App, AI Model, Mobile App"
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="highest_qualification" className={styles.label}>Highest Qualification</label>
              <input
                id="highest_qualification"
                name="highest_qualification"
                type="text"
                value={formData.highest_qualification}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g., MS Computer Science"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="institution_organization" className={styles.label}>Institution / Organization</label>
              <input
                id="institution_organization"
                name="institution_organization"
                type="text"
                value={formData.institution_organization}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g., Google, MIT"
              />
            </div>
          </div>

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <Button type="submit" loading={loading}
            variant="primary"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Saving Profile...' : 'Complete Onboarding'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MentorOnboarding;
